import { Response, createServer } from 'miragejs';

import { default as lines_data } from './test-assets/example-lines.json';

export interface MockServerOptions {
    passthroughUrls?: string[];
    urlPrefix?: string;
}

const searchResultsMock = [
    'd6300dda-707b-431f-bdef-5edbb2d4de55',
    '5768012a-1a97-4e1e-8054-2da8ee726974',
    '0b60e8b0-97a6-4d1a-b3a0-871a9083ebe7',
    '326d86b9-fd26-4967-bce6-20153ab581ea',
    'ced8225b-2219-4ec0-a483-0acd60233f0b',
    'c81feb80-6314-4ef6-ae16-fa6937661aaa',
    '79589641-447b-4e87-9c60-16e379ee6a64',
    '9fe604fa-fa70-424c-ad3a-1f3025cfc90d',
    'cf25f370-83a1-412a-bf0c-d4f41653e131',
    '740938e2-ed87-4e6e-91fb-ec18a3965e46',
    'c2ed7836-1d06-48ce-a8d6-3dedfae5a719',
    'a9d3ef77-80ba-471d-a753-ec75107a1517',
    'a4f14692-7b5c-4dc7-b68d-6be0a0fd0586',
    '0388e2cf-e5be-477c-92d3-5c8aa9760b41',
    '0e7c319f-1fe9-4a29-b4af-236f3f2aac61',
    'b883d9d0-ee7f-4e68-9182-368a6669f5ce',
    '41a9b00b-1eec-48ec-ab77-68d58664198d',
    'a5acc730-83f7-4c13-acd7-7257314349f3',
    'ee5f3e92-2722-41bc-b4c1-f9fec0ff4256',
    '4f1994ba-97d5-4e47-9797-9735a3306254',
    'd557d15f-0259-47d4-b037-9e2d9b53a42e',
    '94ea2b32-80c3-4077-bb64-7765906432d0',
    'a8cbf96e-aa60-4836-8969-02f1aa4e25f4',
    '7e530100-c9e9-4212-8e7b-2820b5e9ad04',
    '5da799ac-2dce-450a-9d07-95d7de69ae0b',
    '78edce98-45d4-43ac-aeff-6f9f75939c81',
    'a1f04d1d-e041-4df5-9fba-a53f9ef963bd',
    '9e1637a1-9ad9-44d9-bea5-8bedfb756e8b',
    '64094c11-f8f9-4919-a451-8bb945a2a99a',
    '44ed2e20-0afa-40da-979d-8b4e73cd1192',
    '3e29a26b-2d6d-46e2-bace-54957516d8f4',
    '0dff8a82-48df-4e3d-a320-0a496bdaaba9',
];

export function createMockServer(options?: MockServerOptions) {
    const passthroughs = options?.passthroughUrls || [];
    return createServer({
        routes() {
            this.namespace = 'api-mock';
            passthroughs.forEach((val) => {
                this.passthrough(val);
            });
            if (options?.urlPrefix) {
                this.urlPrefix = options.urlPrefix;
            }
            this.get('v1/lines', (schema, request) => {
                return lines_data;
            });
            this.get('/v1/search', (schema, request) => {
                if (request.queryParams.ride_uuid === 'd6300dda-707b-431f-bdef-5edbb2d4de54') {
                    return new Response(500, {}, { error: 'Server error' });
                } else if (request.queryParams.ride_uuid === 'a505dda-707b-431f-bdef-5edbb2d4de55') {
                    return new Response(501, {}, { error: 'Not implemented' });
                } else if (request.queryParams.ride_uuid === 'd6300dda-707b-431f-bdef-5edbb2d4de55') {
                    return new Response(404, {}, { error: 'Ride not found' });
                } else if (request.queryParams.ride_uuid === 'd6300dda-707b-431f-bdef-5edbb2d4de') {
                    return new Response(422, {}, { error: 'Malformed user input' });
                } else {
                    return {
                        ride_uuid: request.queryParams.ride_uuid,
                        exists: true,
                    };
                }
            });
            this.get('/v2/search', (schema, request) => {
                if (request.queryParams.line_codes) {
                    if (!request.queryParams.departure_start || !request.queryParams.departure_end) {
                        return new Response(422, {}, { error: 'Missing departure date range' });
                    }
                    if (!request.queryParams.trip_number_filter) {
                        return new Response(422, { error: 'Missing query parameter: trip_number_filter' });
                    }
                    if (request.queryParams.trip_number_filter === 'exact' && !request.queryParams.trip_number) {
                        return new Response(422, { error: 'Missing query parameter: trip_number' });
                    }
                    return {
                        line_code: request.queryParams.line_code,
                        departure_start: request.queryParams.departure_start,
                        departure_end: request.queryParams.departure_end,
                        rides: searchResultsMock,
                    };
                } else {
                    return new Response(422, {}, { error: 'Malformed user input' });
                }
            });
            this.post(
                '/v1/batch/ride-kpis',
                (schema, request) => {
                    const example = {
                        core: {
                            ride_id: 'd6300dda-707b-431f-bdef-5edbb2d4de55',
                            line_code: 'N126',
                            trip_number: 'TN7',
                            start: 'Berlin ZOB',
                            destination: 'Munich ZOB',
                            departure_time: '2024-07-06 15:30:52.249281',
                            departure_timezone: 'Europe/Rome',
                        },
                        pricing: {
                            load: 0.5,
                            demand_load: 0.9,
                            bid_price_active: true,
                            pax_km: 300.5,
                            distance_km: 62.0,
                            revenue_eur: 5432.0,
                        },
                        meta: {
                            data_issue: undefined,
                        },
                    };
                    const body = JSON.parse(request.requestBody);
                    const apiResponse = body.ride_ids.map((ride_id: string) => {
                        const mockRideSummary = {
                            core: { ...example.core },
                            pricing: { ...example.pricing },
                            meta: { ...example.meta },
                        };
                        mockRideSummary.core.ride_id = ride_id;
                        return mockRideSummary;
                    });
                    apiResponse[0].pricing = undefined;
                    apiResponse[0].meta.data_issue = 'overbooked';
                    return apiResponse;
                }
                // { timing: 4000 }
            );
            this.get('/v1/exchange-rates', (schema, request) => {
                const data = [
                    {
                        source_currency: 'EUR',
                        target_currency: 'CZK',
                        exchange_rate: 25.29,
                    },
                    {
                        source_currency: 'EUR',
                        target_currency: 'DKK',
                        exchange_rate: 7.4595,
                    },
                    {
                        source_currency: 'EUR',
                        target_currency: 'CLP',
                        exchange_rate: 1009.125,
                    },
                    {
                        source_currency: 'EUR',
                        target_currency: 'CAD',
                        exchange_rate: 1.4949,
                    },
                    {
                        source_currency: 'EUR',
                        target_currency: 'MKD',
                        exchange_rate: 61.5,
                    },
                    {
                        source_currency: 'EUR',
                        target_currency: 'USD',
                        exchange_rate: 1.0934,
                    },
                ];
                return data.filter((val) => {
                    const matchesSource =
                        !request.queryParams.source_currency ||
                        val.source_currency === request.queryParams.source_currency;
                    return matchesSource;
                });
            });
            this.get('/v1/rides/:ride_uuid', (schema, request) => {
                if (request.params.ride_uuid === 'fail') {
                    return new Response(422, {}, { error: 'Malformed user input' });
                }
                if (request.params.ride_uuid === 'a505dda-707b-431f-bdef-5edbb2d4de55') {
                    return new Response(501, {}, { error: 'Not implemented' });
                } else {
                    return {
                        ride_uuid: request.params.ride_uuid,
                        line_uuid: 'd88ceba7-132a-4aae-90cf-838a88421b41',
                        line_code: 'LM4',
                        trip_number: 'TN33',
                        status: 'on_sale',
                        distance_km: 2.0,
                        revenue_eur: 1.0,
                        curr_average_load: 1.0,
                        pax: 25,
                        pax_km: 10.0,
                        segments: [
                            {
                                from_uuid: '40dde3b8-8646-11e6-9066-549f350fcb0c',
                                from_stop_code: 'HOL8',
                                to_stop_code: 'MID6',
                                to_uuid: '40d8f682-8646-11e6-9066-549f350fcb0c',
                                from_stop_name: 'Hofoldingen 1',
                                to_stop_name: 'Stop 2',
                                from_city_name: 'Stuttgart',
                                to_city_name: 'Munich',
                                from_city_uuid: '55dde3b8-8646-11e6-9066-549f350fcb0c',
                                to_city_uuid: '55d8f682-8646-11e6-9066-549f350fcb0d',
                                departure_time: '2021-01-01T10:10:00.000',
                                departure_timezone: 'Europe/Paris',
                                arrival_time: '2021-01-01T10:30:00.000',
                                arrival_timezone: 'Europe/Paris',
                                distance_km: 1.0,
                                curr_total_capacity: 45,
                                curr_load: 0.8,
                                demand_load: 1.0,
                                bid_price_active: true,
                                turbo_outputs: [
                                    {
                                        pre_booking_day: -1.0,
                                        route_changed: false,
                                        remaining_forecast: 10,
                                        bid_price_eur: 1.0,
                                        remaining_capacity: 49,
                                        total_capacity: 50,
                                    },
                                ],
                            },
                            {
                                from_uuid: '40d8f682-8646-11e6-9066-549f350fcb0c',
                                to_uuid: '80d8f682-8646-11e6-9066-549f350fcb0c',
                                from_stop_code: 'MID6',
                                to_stop_code: 'MUC1',
                                from_stop_name: 'Stop 2',
                                to_stop_name: 'Stop 3',
                                to_city_name: 'Munich',
                                from_city_name: 'Munich',
                                from_city_uuid: '55d8f682-8646-11e6-9066-549f350fcb0d',
                                to_city_uuid: '55d8f682-8646-11e6-9066-549f350fcb0d',
                                departure_time: '2021-01-01T10:40:00.000',
                                departure_timezone: 'Europe/Paris',
                                arrival_time: '2021-01-01T10:55:00.000',
                                arrival_timezone: 'Europe/Paris',
                                distance_km: 1.0,
                                curr_total_capacity: 50,
                                curr_load: 0.4,
                                demand_load: 0.8,
                                bid_price_active: false,
                                turbo_outputs: [
                                    {
                                        pre_booking_day: -1.0,
                                        route_changed: false,
                                        remaining_forecast: 10,
                                        bid_price_eur: 1,
                                        remaining_capacity: 49,
                                        total_capacity: 50,
                                    },
                                    {
                                        pre_booking_day: -1.2,
                                        route_changed: false,
                                        remaining_forecast: 10,
                                        bid_price_eur: 1.5,
                                        remaining_capacity: 49,
                                        total_capacity: 50,
                                    },
                                ],
                            },
                            {
                                from_uuid: '80d8f682-8646-11e6-9066-549f350fcb0c',
                                to_uuid: '10c8f682-8646-11e6-9066-549f350fcb0c',
                                from_stop_code: 'MUC1',
                                to_stop_code: 'MUC2',
                                from_stop_name: 'Stop 3',
                                to_stop_name: 'Stop 4',
                                to_city_name: 'Munich',
                                from_city_name: 'Munich',
                                from_city_uuid: '55d8f682-8646-11e6-9066-549f350fcb0d',
                                to_city_uuid: '55d8f682-8646-11e6-9066-549f350fcb0d',
                                departure_time: '2021-01-01T11:00:00.000',
                                departure_timezone: 'Europe/Paris',
                                arrival_time: '2021-01-01T11:48:00.000',
                                arrival_timezone: 'Europe/Paris',
                                distance_km: 1.0,
                                curr_total_capacity: 50,
                                curr_load: 1.0,
                                demand_load: 1.0,
                                bid_price_active: false,
                                turbo_outputs: [
                                    {
                                        pre_booking_day: -1.0,
                                        route_changed: false,
                                        remaining_forecast: 10,
                                        bid_price_eur: 1,
                                        remaining_capacity: 49,
                                        total_capacity: 50,
                                    },
                                ],
                            },
                            {
                                from_uuid: '10c8f682-8646-11e6-9066-549f350fcb0c',
                                to_uuid: '11b8f682-8646-11e6-9066-549f350fcb0c',
                                from_stop_code: 'MUC2',
                                to_stop_code: 'MUC3',
                                from_stop_name: 'Stop 4',
                                to_stop_name: 'Stop 5',
                                to_city_name: 'Munich',
                                from_city_name: 'Munich',
                                from_city_uuid: '55d8f682-8646-11e6-9066-549f350fcb0d',
                                to_city_uuid: '55d8f682-8646-11e6-9066-549f350fcb0d',
                                departure_time: '2021-01-01T12:05:00.000',
                                departure_timezone: 'Europe/Paris',
                                arrival_time: '2021-01-01T12:37:00.000',
                                arrival_timezone: 'Europe/Paris',
                                distance_km: 1.0,
                                curr_total_capacity: 50,
                                curr_load: 1.0,
                                demand_load: 1.0,
                                bid_price_active: true,
                                turbo_outputs: [
                                    {
                                        pre_booking_day: -1.0,
                                        route_changed: false,
                                        remaining_forecast: 10,
                                        bid_price_eur: 1,
                                        remaining_capacity: 49,
                                        total_capacity: 50,
                                    },
                                ],
                            },
                            {
                                from_uuid: '11b8f682-8646-11e6-9066-549f350fcb0c',
                                to_uuid: '99b8f682-8646-11e6-9066-549f350fcb0c',
                                from_stop_code: 'MUC3',
                                to_stop_code: 'MUC4',
                                from_stop_name: 'Stop 5',
                                to_stop_name: 'Stop 6',
                                to_city_name: 'Munich',
                                from_city_name: 'Munich',
                                from_city_uuid: '55d8f682-8646-11e6-9066-549f350fcb0d',
                                to_city_uuid: '55d8f682-8646-11e6-9066-549f350fcb0d',
                                departure_time: '2021-01-01T12:45:00.000',
                                departure_timezone: 'Europe/Paris',
                                arrival_time: '2021-01-01T13:20:00.000',
                                arrival_timezone: 'Europe/Paris',
                                distance_km: 3.5,
                                curr_total_capacity: 50,
                                curr_load: 0.2,
                                demand_load: 0.4,
                                bid_price_active: false,
                                turbo_outputs: [
                                    {
                                        pre_booking_day: -1.0,
                                        route_changed: false,
                                        remaining_forecast: 10,
                                        bid_price_eur: 1,
                                        remaining_capacity: 49,
                                        total_capacity: 50,
                                    },
                                ],
                            },
                            {
                                from_uuid: '99b8f682-8646-11e6-9066-549f350fcb0c',
                                to_uuid: '99c8f682-8646-11e6-9066-549f350fcb0c',
                                from_stop_code: 'MUC4',
                                to_stop_code: 'MUC5',
                                from_stop_name: 'Stop 6',
                                to_stop_name: 'Stop 7',
                                to_city_name: 'Munich',
                                from_city_name: 'Munich',
                                from_city_uuid: '55d8f682-8646-11e6-9066-549f350fcb0d',
                                to_city_uuid: '55d8f682-8646-11e6-9066-549f350fcb0d',
                                departure_time: '2021-01-01T13:30:00.000',
                                departure_timezone: 'Europe/Paris',
                                arrival_time: '2021-01-01T14:08:00.000',
                                arrival_timezone: 'Europe/Paris',
                                distance_km: 1.0,
                                curr_total_capacity: 50,
                                curr_load: 1.0,
                                demand_load: 1.0,
                                bid_price_active: true,
                                turbo_outputs: [
                                    {
                                        pre_booking_day: -1.0,
                                        route_changed: false,
                                        remaining_forecast: 10,
                                        bid_price_eur: 1,
                                        remaining_capacity: 49,
                                        total_capacity: 50,
                                    },
                                ],
                            },
                            {
                                from_uuid: '99c8f682-8646-11e6-9066-549f350fcb0c',
                                to_uuid: '99f0f682-8646-11e6-9066-549f350fcb0c',
                                from_stop_code: 'MUC5',
                                to_stop_code: 'LON',
                                from_stop_name: 'Stop 7',
                                to_stop_name: 'London Heathrow Airport',
                                to_city_name: 'London',
                                from_city_name: 'Munich',
                                from_city_uuid: '55d8f682-8646-11e6-9066-549f350fcb0d',
                                to_city_uuid: '69a8f682-8646-11e6-9066-549f350fcb0d',
                                departure_time: '2021-01-01T18:12:00.000',
                                departure_timezone: 'Europe/Paris',
                                arrival_time: '2021-01-01T20:44:00.000',
                                arrival_timezone: 'Europe/London',
                                distance_km: 1.0,
                                curr_total_capacity: 50,
                                curr_load: 0.5,
                                demand_load: 0.5,
                                bid_price_active: false,
                                turbo_outputs: [
                                    {
                                        pre_booking_day: -1.0,
                                        route_changed: false,
                                        remaining_forecast: 10,
                                        bid_price_eur: 1,
                                        remaining_capacity: 49,
                                        total_capacity: 50,
                                    },
                                ],
                            },
                        ],
                        relations: [
                            {
                                from_uuid: '99c8f682-8646-11e6-9066-549f350fcb0c',
                                to_uuid: '99f0f682-8646-11e6-9066-549f350fcb0c',
                                distance_km: 2.0,
                                revenue_eur: 1.0,
                                orders: [
                                    {
                                        pre_booking_day: 375.0,
                                        price_eur: 11.0,
                                        booking_class: 'B90',
                                    },
                                    {
                                        pre_booking_day: 370.0,
                                        price_eur: 12.5,
                                        booking_class: 'B89',
                                    },
                                    {
                                        pre_booking_day: 325.0,
                                        price_eur: 15.5,
                                        booking_class: 'B63',
                                    },
                                    {
                                        pre_booking_day: 321.0,
                                        price_eur: 15.5,
                                        booking_class: 'B63',
                                    },
                                    {
                                        pre_booking_day: 100.0,
                                        price_eur: 45.72,
                                        booking_class: 'B43',
                                    },
                                    {
                                        pre_booking_day: 100.0,
                                        price_eur: 54.6,
                                        booking_class: 'B39',
                                    },
                                    {
                                        pre_booking_day: 16.0,
                                        price_eur: 65.0,
                                        booking_class: 'B25',
                                    },
                                    {
                                        pre_booking_day: 10.0,
                                        price_eur: 74.2,
                                        booking_class: 'B15',
                                    },
                                    {
                                        pre_booking_day: 10.3,
                                        price_eur: 76.2,
                                        booking_class: 'B16',
                                    },
                                    {
                                        pre_booking_day: 5.5,
                                        price_eur: 104.2,
                                        booking_class: 'B11',
                                    },
                                    {
                                        pre_booking_day: 5.5,
                                        price_eur: 105.2,
                                        booking_class: 'B11',
                                    },
                                    {
                                        pre_booking_day: 5.8,
                                        price_eur: 122.2,
                                        booking_class: 'B9',
                                    },
                                    {
                                        pre_booking_day: 5.9,
                                        price_eur: 129.3,
                                        booking_class: 'B8',
                                    },
                                    {
                                        pre_booking_day: 3.1,
                                        price_eur: 139.8,
                                        booking_class: 'B5',
                                    },
                                ],
                                curr_max_load: 0.5,
                                pax: 25,
                                max_demand_load: 1.0,
                                remaining_demand_revenue_eur: 10.0,
                                turbo_outputs: [
                                    {
                                        pre_booking_day: -1.0,
                                        route_changed: false,
                                        remaining_forecast_per_price_label: [
                                            {
                                                price_label: 'B20',
                                                nominal_price_eur: 41.0,
                                                remaining_forecast: 44.0,
                                            },
                                            {
                                                price_label: 'B10',
                                                nominal_price_eur: 31.0,
                                                remaining_forecast: 34.0,
                                            },
                                        ],
                                        lowest_efficient_fare_eur: 1.0,
                                        lowest_efficient_fare_label: 'B10',
                                        bid_price_eur: 2.0,
                                        scaling_factor: 1.2,
                                    },
                                    {
                                        pre_booking_day: -1.2,
                                        route_changed: false,
                                        remaining_forecast_per_price_label: [
                                            {
                                                price_label: 'B20',
                                                nominal_price_eur: 41.0,
                                                remaining_forecast: 44.0,
                                            },
                                            {
                                                price_label: 'B10',
                                                nominal_price_eur: 31.0,
                                                remaining_forecast: 34.0,
                                            },
                                        ],
                                        lowest_efficient_fare_eur: 1.0,
                                        lowest_efficient_fare_label: 'B10',
                                        bid_price_eur: 2.0,
                                        scaling_factor: 3.4,
                                    },
                                ],
                            },
                            {
                                from_uuid: '40dde3b8-8646-11e6-9066-549f350fcb0c', // HOL8 (first segment start)
                                to_uuid: '10c8f682-8646-11e6-9066-549f350fcb0c', // MUC2 (third segment end)
                                distance_km: 3.0, // Sum of segments 1, 2, and 3 (1.0 + 1.0 + 1.0)
                                revenue_eur: 456.9, // Sum of all order prices below
                                orders: [
                                    {
                                        pre_booking_day: 380.0,
                                        price_eur: 8.5,
                                        booking_class: 'B91',
                                    },
                                    {
                                        pre_booking_day: 92.0,
                                        price_eur: 12.0,
                                        booking_class: 'B78',
                                    },
                                    {
                                        pre_booking_day: 78.0,
                                        price_eur: 14.0,
                                        booking_class: 'B64',
                                    },
                                    {
                                        pre_booking_day: 64.0,
                                        price_eur: 16.5,
                                        booking_class: 'B55',
                                    },
                                    {
                                        pre_booking_day: 50.0,
                                        price_eur: 18.9,
                                        booking_class: 'B50',
                                    },
                                    {
                                        pre_booking_day: 43.0,
                                        price_eur: 22.0,
                                        booking_class: 'B48',
                                    },
                                    {
                                        pre_booking_day: 36.0,
                                        price_eur: 25.5,
                                        booking_class: 'B45',
                                    },
                                    {
                                        pre_booking_day: 29.0,
                                        price_eur: 28.0,
                                        booking_class: 'B42',
                                    },
                                    {
                                        pre_booking_day: 25.0,
                                        price_eur: 32.0,
                                        booking_class: 'B38',
                                    },
                                    {
                                        pre_booking_day: 22.0,
                                        price_eur: 35.5,
                                        booking_class: 'B35',
                                    },
                                    {
                                        pre_booking_day: 19.0,
                                        price_eur: 38.0,
                                        booking_class: 'B32',
                                    },
                                    {
                                        pre_booking_day: 17.0,
                                        price_eur: 42.5,
                                        booking_class: 'B28',
                                    },
                                    {
                                        pre_booking_day: 15.0,
                                        price_eur: 45.0,
                                        booking_class: 'B26',
                                    },
                                    {
                                        pre_booking_day: 13.0,
                                        price_eur: 48.0,
                                        booking_class: 'B23',
                                    },
                                    {
                                        pre_booking_day: 11.0,
                                        price_eur: 52.5,
                                        booking_class: 'B20',
                                    },
                                    {
                                        pre_booking_day: 9.5,
                                        price_eur: 56.0,
                                        booking_class: 'B18',
                                    },
                                    {
                                        pre_booking_day: 8.2,
                                        price_eur: 61.4,
                                        booking_class: 'B15',
                                    },
                                    {
                                        pre_booking_day: 7.1,
                                        price_eur: 68.0,
                                        booking_class: 'B12',
                                    },
                                    {
                                        pre_booking_day: 6.3,
                                        price_eur: 75.5,
                                        booking_class: 'B10',
                                    },
                                    {
                                        pre_booking_day: 5.2,
                                        price_eur: 82.0,
                                        booking_class: 'B8',
                                    },
                                    {
                                        pre_booking_day: 4.5,
                                        price_eur: 89.0,
                                        booking_class: 'B6',
                                    },
                                    {
                                        pre_booking_day: 3.8,
                                        price_eur: 95.5,
                                        booking_class: 'B5',
                                    },
                                    {
                                        pre_booking_day: 2.9,
                                        price_eur: 102.0,
                                        booking_class: 'B4',
                                    },
                                    {
                                        pre_booking_day: 1.7,
                                        price_eur: 108.5,
                                        booking_class: 'B2',
                                    },
                                    {
                                        pre_booking_day: 0.8,
                                        price_eur: 115.0,
                                        booking_class: 'B1',
                                    },
                                ],
                                curr_max_load: 0.8,
                                pax: 35,
                                max_demand_load: 1.0,
                                remaining_demand_revenue_eur: 15.0,
                                turbo_outputs: [
                                    {
                                        pre_booking_day: -1.0,
                                        route_changed: false,
                                        remaining_forecast_per_price_label: [
                                            {
                                                price_label: 'B25',
                                                nominal_price_eur: 32.0,
                                                remaining_forecast: 28.0,
                                            },
                                            {
                                                price_label: 'B15',
                                                nominal_price_eur: 45.0,
                                                remaining_forecast: 18.0,
                                            },
                                        ],
                                        lowest_efficient_fare_eur: 22.0,
                                        lowest_efficient_fare_label: 'B25',
                                        bid_price_eur: 24.0,
                                        scaling_factor: 1.1,
                                    },
                                ],
                            },
                        ],
                        last_optimized_at: '2024-01-01T00:00:00.000Z',
                    };
                }
            });
            this.get('/v1/feature-flags', () => {
                return {
                    yield_table: true,
                    bid_price_filter: true,
                };
            });
            this.get('/', () => {
                return { Hello: 'RideHub' };
            });
        },
    });
}
